import { createTheme, type Theme } from '@mui/material/styles';

import {
  brand,
  elevation,
  mdSysColor,
  paletteDark,
  paletteLight,
  radius,
  spacing,
  stateLayer,
  tap,
  typography,
  type ColorMode,
} from './tokens.js';

/**
 * MUI theme constructed from FSE tokens, per color mode.
 *
 * This is the only file in @fse/ui that depends on MUI's theme API.
 * Swap recipe: rewrite this file (and ThemeProvider.tsx) to map
 * `tokens.ts` to whatever library replaces MUI.
 */
export function buildTheme(mode: ColorMode): Theme {
  const p = mode === 'dark' ? paletteDark : paletteLight;
  // Pick the "main" primary shade per-mode so foreground contrast holds:
  // light mode uses the deep brand green (700); dark mode uses a brighter
  // mid (400) that reads cleanly on the charcoal surface.
  const primaryMain = mode === 'dark' ? p.primary[400] : p.primary[700];
  const secondaryMain = p.secondary[500];
  const t = typography;
  const display = typography.fontFamilyDisplay;

  return createTheme({
    palette: {
      mode,
      primary: {
        light: p.primary[300],
        main: primaryMain,
        dark: p.primary[800],
        contrastText: mode === 'dark' ? brand.leaf[900] : brand.paper[50],
      },
      secondary: {
        light: p.secondary[300],
        main: secondaryMain,
        dark: p.secondary[700],
        contrastText: p.secondary.contrast,
      },
      error: { main: p.semantic.danger },
      warning: { main: p.semantic.warning },
      info: { main: p.semantic.info },
      success: { main: p.semantic.success },
      background: {
        default: p.neutral.bg,
        paper: p.neutral.surface,
      },
      text: {
        primary: p.neutral.text,
        secondary: p.neutral.textMuted,
        disabled: p.neutral.textDisabled,
      },
      divider: p.neutral.border,
    },
    typography: {
      fontFamily: typography.fontFamily,
      // Headings use the display family (Bricolage Grotesque); body/labels use
      // Noto Sans. Sizes follow the MD3 scale (rural-bumped) from tokens.
      h1: { fontFamily: display, letterSpacing: '-0.01em', ...t.displaySmall },
      h2: { fontFamily: display, letterSpacing: '-0.01em', ...t.headlineLarge },
      h3: { fontFamily: display, letterSpacing: '-0.01em', ...t.headlineMedium },
      h4: { fontFamily: display, letterSpacing: '-0.005em', ...t.headlineSmall },
      h5: { fontFamily: display, ...t.titleLarge },
      h6: { fontFamily: display, ...t.titleMedium },
      subtitle1: { ...t.titleMedium },
      subtitle2: { ...t.titleSmall },
      body1: { ...t.bodyLarge },
      body2: { ...t.bodyMedium },
      caption: { ...t.bodySmall },
      button: {
        fontFamily: display,
        fontSize: t.labelLarge.fontSize,
        fontWeight: 600,
        textTransform: 'none' as const,
      },
      overline: { ...t.labelSmall, textTransform: 'uppercase' as const },
    },
    shape: {
      borderRadius: radius.md,
    },
    spacing: spacing.xs,
    components: {
      // Visible focus ring everywhere — accessibility floor for the rural,
      // keyboard- and screen-reader-using audience (design: 3px leaf ring).
      MuiCssBaseline: {
        styleOverrides: {
          ['a:focus-visible, button:focus-visible, [role="button"]:focus-visible, ' +
          'input:focus-visible, textarea:focus-visible, select:focus-visible, ' +
          '[tabindex]:focus-visible']: {
            outline: `3px solid ${stateLayer.focus}`,
            outlineOffset: '2px',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          // Pill buttons (MD3 full corner), display-font bold label (design:
          // ui_kits/farmer-app/components/Atoms.jsx), rural touch targets.
          root: {
            textTransform: 'none',
            fontFamily: display,
            fontWeight: 700,
            borderRadius: radius.full,
          },
          sizeLarge: {
            minHeight: tap.lg, // 64dp primary CTA
            paddingInline: spacing.lg,
            fontSize: t.titleSmall.fontSize,
          },
          sizeMedium: { minHeight: tap.md, paddingInline: spacing.md }, // 56dp per preview/buttons.html
          sizeSmall: { minHeight: tap.sm }, // 44dp
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small', fullWidth: true },
      },
      MuiOutlinedInput: {
        // Design spec (preview/text-field.html): 4dp corner, 56dp min-height,
        // 18px value text. We keep MUI's outlined focus ring + floating-label
        // notch behaviour — only the corner / height / value-size are pinned.
        styleOverrides: {
          root: { borderRadius: radius.sm, minHeight: tap.md }, // 4px corner, 56dp
          input: { fontSize: t.bodyLarge.fontSize }, // 18px value text
        },
      },
      MuiSnackbarContent: {
        // Design spec (preview/snackbars.html): MD3 inverse snackbar —
        // inverseSurface bg, inverseOnSurface text, 4dp corner, level-3 shadow,
        // inversePrimary action label. Applies to the default (message+action)
        // snackbar only; Alert-child snackbars keep the Alert's own colours.
        styleOverrides: {
          root: {
            backgroundColor: mdSysColor[mode].inverseSurface,
            color: mdSysColor[mode].inverseOnSurface,
            borderRadius: radius.sm,
            boxShadow: elevation.level3,
            fontSize: t.titleSmall.fontSize, // 15px message text
          },
          action: {
            color: mdSysColor[mode].inversePrimary,
          },
        },
      },
      MuiCard: {
        // elevation 0 keeps MUI's overlay logic off; the design card spec
        // (README "Cards": surfaceContainerLow tier, 12dp cornerMedium,
        // level-1 shadow) is applied via styleOverrides instead.
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: radius.lg,
            backgroundColor: mdSysColor[mode].surfaceContainerLow,
            boxShadow: elevation.level1,
            // Stroke allowed: the card sits on the same tonal level as its parent.
            border: `1px solid ${p.neutral.border}`,
          },
        },
      },
    },
  });
}

/** Legacy export — pre-dark-mode code reaches for `fseTheme` directly. */
export const fseTheme = buildTheme('light');
